import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer';
import Geocode from "react-geocode";

import Preloader from '../Preloader/Preloader'

import { ExcursionType } from 'api/types'

import excursionAPI from '../../api/excursionApi';

import './Travels.css';
import AuthModal from './../AuthModal/AuthModal';
import { prepareExcursion } from 'hooks/useExcursion';
import { useCookies } from 'react-cookie'
import { debounce } from 'lodash';

Geocode.setApiKey("AIzaSyBn_3KFAty9xlaD2nBeCrTSWniUFdLp2Zc");

export type StepType = 'loader' | 'travels';


const Travels = ({
    external,
    needRoutes,
    search,
    routesCount,
    title
}: {
    external: boolean,
    needRoutes?: boolean,
    routesCount?: number,
    title?: string,
    search?: boolean,
}) => {

    const { t } = useTranslation();
    const [cookies] = useCookies();
    const { ref: lastTravelRef, inView: lastTravelVisible } = useInView();

    const [lastPage, setLastPage] = useState<number>(0);
    const [gettingElements, setGettingElements] = useState<boolean>(false);
    const [gettingAllElements, setGettingAllElements] = useState<boolean>(false);
    const [positionGot, setPositionGot] = useState<boolean>(false);
    const [loaderMessage, setLoaderMessage] = useState<string>(t('EXCURSIONS.STATUS.GET_INFO'));
    const [currentScreen, setCurrentScreen] = useState<StepType>('loader');
    const [excurisionsInfo, setExcursionsInfo] = useState<ExcursionType[]>();
    const [userPosition, setUserPosition] = useState<{ lat: number, lng: number }>();
    const [searchPosition, setSearchPosition] = useState<{ lat: number, lng: number }>();

    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authCallback, setAuthCallback] = useState<() => void>();
    const [authCloseCallback, setAuthCloseCallback] = useState<() => void>();

    const { uid, sid } = cookies;

    const CABINET_URL = window.location.host.indexOf('.local') === -1
        && window.location.host.indexOf('localhost') === -1
        ? 'https://st.2aw.io/'
        : 'http://cabinet.2aw.local/';

    function getRandomInRange(from: number, to: number, fixed: number) {
        return +(Math.random() * (to - from) + from).toFixed(fixed);
    }

    useEffect(() => {
        if (positionGot)
            return;

        if (external) {
            setUserPosition({
                lat: getRandomInRange(51, 64, 5),
                lng: getRandomInRange(21, 117, 5)
            });
            setPositionGot(true);
            return;
        }

        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition((position) => {
                if (!positionGot) {
                    setUserPosition({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                    setPositionGot(true);
                }
            }, (error) => {
                setUserPosition({
                    lat: 55.753995,
                    lng: 37.614069,
                });
                setPositionGot(true);
            });
        } else {
            setUserPosition({
                lat: 55.753995,
                lng: 37.614069,
            });
            setPositionGot(true);
        }
    }, [positionGot]);

    const getEntities = useCallback((userPosition: { lat: number, lng: number }, page?: number) => {
        if (gettingElements)
            return;

        page = page === undefined ? lastPage : page;

        const getExcursions = async () => {
            setGettingElements(true);

            const excursionsInfoRes = external
                ? await excursionAPI.getExcursions(undefined, undefined, page)
                : await excursionAPI.getExcursions(uid, sid, page);

            if (
                excursionsInfoRes.data
                && excursionsInfoRes.data.excursions
                && excursionsInfoRes.data.excursions.length
            ) {
                const prevEx = excurisionsInfo || [];
                setExcursionsInfo([...prevEx, ...excursionsInfoRes.data.excursions]);
                setCurrentScreen('travels');
            } else {
                setLoaderMessage(t('NOT_FOUND.STREAMS'));
                setGettingAllElements(true);
            }

            setGettingElements(false);
        }

        const getRoutes = async () => {
            setGettingElements(true);

            let routes = await excursionAPI.getRoutes(userPosition.lat, userPosition.lng, page);
            if (routesCount) {
                routes = routes.slice(0, routesCount);
                setGettingAllElements(true);
            }
            if (routes.length) {
                const prevRoutes = excurisionsInfo || [];
                const routesInfo = [...prevRoutes, ...routes];

                setExcursionsInfo(routesInfo);
                setCurrentScreen('travels');
            } else {
                if (!excurisionsInfo) {
                    setLoaderMessage(t('NOT_FOUND.ROUTES'));
                }

                setGettingAllElements(true);
            }

            setGettingElements(false);
        }

        if (needRoutes) {
            getRoutes();
        } else {
            getExcursions();
        }


        setLastPage(++page);
    }, [gettingElements, excurisionsInfo, lastPage]);

    React.useEffect(() => {
        if (!excurisionsInfo && userPosition) {
            getEntities(userPosition);
        }
    }, [excurisionsInfo, userPosition]);

    useEffect(() => {
        if (userPosition && lastTravelVisible && !gettingAllElements) {
            const position = searchPosition || userPosition;
            getEntities(position);
        }
    }, [lastTravelVisible, userPosition, searchPosition, gettingAllElements]);

    const searchPlace = useCallback(debounce((search: string) => {
        if (search) {
            Geocode.fromAddress(search).then(
                (response) => {
                    if (response.status === 'OK' && response.results.length > 0) {
                        const { lat, lng } = response.results[0].geometry.location;
                        setExcursionsInfo([]);
                        setSearchPosition({
                            lat: lat,
                            lng: lng
                        });
                        getEntities({
                            lat: lat,
                            lng: lng
                        }, 0);
                    }
                },
                (error) => {
                    // toast.error(error);
                }
            );
        } else {
            setExcursionsInfo([]);
            setSearchPosition(undefined);

            if (userPosition) {
                getEntities(userPosition, 0);
            }
        }

        setGettingAllElements(false);
    }, 500), [userPosition]);

    return (
        <>
            {currentScreen === 'loader' && (
                <Preloader message={loaderMessage} />
            )}

            {currentScreen === 'travels' && title && (
                <h3 className='title travels__title'>
                    {title}
                </h3>
            )}


            {currentScreen === 'travels' && search && (
                <input
                    type="text"
                    className='travels__search'
                    placeholder={t('COMPONENT.MAP.SEARCH.PLACEHOLDER')}
                    onInput={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const search = event.target.value;
                        searchPlace(search);
                    }}
                ></input>
            )}

            <div className='travels'>
                {currentScreen === 'travels' && excurisionsInfo && (
                    excurisionsInfo.map((excursion) => {
                        const excursionPrepared = prepareExcursion(excursion);

                        return (
                            <div
                                key={excursionPrepared.ex_tid}
                                className='travel'
                                onClick={() => {
                                    if (external) {
                                        if (cookies.uid && cookies.sid) {
                                            location.href = CABINET_URL + `?uid=${cookies.uid}&sid=${cookies.sid}&return_url=/ex/?trvl=${excursionPrepared.ex_tid}`;
                                        } else {
                                            setShowAuthModal(true);
                                            setAuthCloseCallback(() => {
                                                return () => {
                                                    setShowAuthModal(false);
                                                }
                                            });
                                            setAuthCallback(() => {
                                                return (uid?: string, sid?: string) => {
                                                    if (uid && sid) {
                                                        location.href = CABINET_URL + `?uid=${uid}&sid=${sid}&return_url=/ex/?trvl=${excursionPrepared.ex_tid}`;
                                                    }
                                                }
                                            });
                                        }
                                    }
                                }}
                            >
                                {!external && (
                                    <Link
                                        className='travel__link'
                                        target='_blank'
                                        to={'/ex/?trvl=' + excursionPrepared.ex_tid}
                                    >
                                    </Link>
                                )}

                                <div className='travel__image'>
                                    <img src={excursionPrepared.firstImageUrl} loading='lazy'></img>
                                </div>

                                <div className='travel__content-wrapper'>
                                    <div className='travel__top'>
                                        <div className='travel__title'>
                                            {excursionPrepared.ex_name}
                                        </div>

                                        <div className='travel__guide'>
                                            {excursionPrepared.ex_guide}
                                        </div>
                                    </div>

                                    <div className='travel__description'>
                                        {excursionPrepared.ex_description}
                                    </div>

                                    {needRoutes && excursionPrepared.ex_description_ext && (
                                        <div className='travel__description_ext'>
                                            {excursionPrepared.ex_description_ext}
                                        </div>
                                    )}

                                    {!needRoutes && (
                                        <div className='travel__bottom'>
                                            <div className='travel__time'>
                                                {excursionPrepared.start_time}
                                            </div>

                                            <div className='travel__price'>
                                                {excursionPrepared.price_formatted}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    })
                )}
            </div>

            {currentScreen === 'travels' && (
                <span ref={lastTravelRef}>
                    {gettingElements && (
                        <div className='travels__loading'></div>
                    )}
                </span>
            )}

            {showAuthModal && (
                <AuthModal
                    callback={authCallback}
                    closeCallback={authCloseCallback}
                />
            )}
        </>
    )
}

export default Travels;